import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';
import { collection, query, where, orderBy, limit, getDocs, getCountFromServer, Timestamp, updateDoc, doc, onSnapshot } from 'firebase/firestore';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button'; // Ensure Button is imported
import { LoadingSpinner } from '../components/ui/loading-spinner';
import { ROUTES } from '../App';
import { format, isPast, formatDistanceToNow, differenceInDays } from 'date-fns';
import NotificationDisplay from '../components/dashboard/NotificationDisplay';
import RegistrationRenewalModal from '../components/dashboard/RegistrationRenewalModal'; 
import { FileText, Receipt, History, BarChart2, Calculator, CreditCard, AlertCircle, ExternalLink, Star, Package, Clock, ShieldCheck, Edit, BookOpen } from 'lucide-react';
import { useTheme } from '../components/ui/theme-provider'; // Import useTheme
import { cn } from '../lib/utils'; // Import cn utility
import { Alert, AlertTitle, AlertDescription } from "../components/ui/alert";
import { X as CloseIcon, AlertTriangle } from "lucide-react";
import { useToast } from "../hooks/use-toast"; // Import useToast
import { decryptData } from '../encryptionUtil'; // Ensure decryptData is imported

// Define calculateStatus at the top level, outside of any component
const calculateStatus = (expiryDate) => {
  if (!expiryDate) return { text: 'Not Set', colorClass: 'bg-gray-500', daysRemaining: null, urgency: 'none', prompt: 'Set expiry date' };
  
  // Ensure expiryDate is a JS Date object
  const date = expiryDate.toDate ? expiryDate.toDate() : new Date(expiryDate);
  if (isNaN(date.getTime())) { // Check for invalid date
    if (process.env.NODE_ENV === 'development') {
      console.warn("calculateStatus received an invalid date:", expiryDate);
    }
    return { text: 'Invalid Date', colorClass: 'bg-gray-500', daysRemaining: null, urgency: 'error', prompt: 'Date is invalid' };
  }
  const daysRemaining = differenceInDays(date, new Date());

  // Add explicit prompts based on urgency
  if (daysRemaining < 0) return { text: 'Expired', colorClass: 'bg-red-700 text-white', daysRemaining, urgency: 'expired', prompt: 'Registration expired!' };
  if (daysRemaining < 7) return { text: 'Urgent', colorClass: 'bg-red-600 text-white', daysRemaining, urgency: 'urgent', prompt: 'Renew immediately!' };
  if (daysRemaining < 30) return { text: 'Expires Soon', colorClass: 'bg-red-500 text-white', daysRemaining, urgency: 'soon', prompt: 'Renewal needed soon.' };
  if (daysRemaining <= 60) return { text: 'Renew Soon', colorClass: 'bg-yellow-500 text-black', daysRemaining, urgency: 'upcoming', prompt: 'Consider renewing soon.' };
  return { text: 'Active', colorClass: 'bg-green-500 text-white', daysRemaining, urgency: 'active', prompt: null }; // No prompt needed for active
};

// Accept currentUser prop
const DashboardPage = ({ user, currentUser, subscriptionStatus }) => {
  const { toast } = useToast(); // Add toast definition
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [totalInvoiceCount, setTotalInvoiceCount] = useState(0);
  const [loadingTotalCount, setLoadingTotalCount] = useState(false);
  const [error, setError] = useState(null);
  // Store individual registration fields from currentUser
  const [gdcExpiry, setGdcExpiry] = useState(null);
  const [gdcProvider, setGdcProvider] = useState(null);
  const [insuranceExpiry, setInsuranceExpiry] = useState(null);
  const [insuranceProvider, setInsuranceProvider] = useState(null);

  const [loadingRegistrations, setLoadingRegistrations] = useState(true);
  const [isRegistrationModalOpen, setIsRegistrationModalOpen] = useState(false);
  const [currentRegistrationType, setCurrentRegistrationType] = useState(null);
  // Replace single alert state with an array to hold multiple active alerts
  const [activeRegistrationAlerts, setActiveRegistrationAlerts] = useState([]);
  const { theme } = useTheme(); // Get theme
  const navigate = useNavigate();
  const location = useLocation(); // Add this line

  const isPro = useMemo(() => 
    subscriptionStatus?.subscription?.level === 'pro' && 
    (subscriptionStatus?.subscription?.status === 'active' || subscriptionStatus?.subscription?.status === 'trialing'), 
    [subscriptionStatus]
  );

  const isSubscriptionEffectivelyFree = useMemo(() => {
    const sub = subscriptionStatus?.subscription;
    if (!sub) return true; // Assume free if no subscription data
    // CRITICAL FIX: Include past_due in effectively free logic but preserve plan level
    return sub.level === 'free' || 
           sub.status === 'ended' || 
           sub.status === 'canceled' || 
           sub.status === 'incomplete_expired' ||
           sub.status === 'past_due'; // ADDED: Treat past_due as effectively free
  }, [subscriptionStatus]);

  // Add a derived flag for Stripe payment success
  const showStripeSyncNote = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('status') === 'success';
  }, [location.search]);

  useEffect(() => {
    if (user?.uid) {
      const fetchDashboardData = async () => {
        if (isPro) setLoadingInvoices(true);        setLoadingTotalCount(true);
        setError(null);
        if (process.env.NODE_ENV === 'development') {
          console.log(`Dashboard: Fetching data for user ${user.uid}`);
        }

        // --- Fetch Recent Invoices (Only if Pro) ---
        if (isPro) {
          try {
            const invoicesRef = collection(db, 'invoices');
            const qInvoices = query(
              invoicesRef,
              where('userId', '==', user.uid),
              orderBy('createdAt', 'desc'),   // Order by creation date              limit(10) // Fetch 10 to filter (Reduced from 15)
            );
            if (process.env.NODE_ENV === 'development') {
              console.log("Dashboard: Executing recent invoices query (orderBy createdAt DESC, limit 10)...");
            }            const invoiceSnapshot = await getDocs(qInvoices);
            if (process.env.NODE_ENV === 'development') {
              console.log(`Dashboard: Query returned ${invoiceSnapshot.size} documents before filtering.`);
            }

            // Map and Filter client-side
            const allFetchedInvoices = invoiceSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            // Filter out invoices where status is explicitly 'deleted'
            const nonDeletedInvoices = allFetchedInvoices.filter(invoice => invoice.status !== 'deleted');
            const finalRecentInvoices = nonDeletedInvoices.slice(0, 5); // Take the first 5 non-deleted            // Log the first document's data AFTER filtering to check fields
            if (finalRecentInvoices.length > 0) {
              const firstDocData = finalRecentInvoices[0];
              if (process.env.NODE_ENV === 'development') {
                console.log("Dashboard: First NON-DELETED invoice data sample:", {
                  id: firstDocData.id,
                  status: firstDocData.status, // Check status (should not be 'deleted')
                  createdAt: firstDocData.createdAt,
                  userId: firstDocData.userId,
                  isPaid: firstDocData.isPaid,
                  practiceDetails: firstDocData.practiceDetails
                });
              }
            }            setRecentInvoices(finalRecentInvoices);
            if (process.env.NODE_ENV === 'development') {
              console.log(`Dashboard: Successfully mapped and filtered invoices. Displaying ${finalRecentInvoices.length}.`);
            }
          } catch (err) {
            console.error("Dashboard: Error fetching recent invoices:", err);
            if (err.code === 'failed-precondition') {
              // Update index suggestion
              setError("Could not load recent invoices. Firestore index might be missing (userId ASC, createdAt DESC). Check console for a link.");
            } else {
              setError("Could not load recent invoices. Please check console for details.");
            }
          } finally {
            setLoadingInvoices(false);
          }
        }

        // --- Fetch Total Invoice Count (All Users, including deleted) ---
        try {
          const invoicesCol = collection(db, 'invoices');          const totalQuery = query(
            invoicesCol,
            where('userId', '==', user.uid)
          );

          if (process.env.NODE_ENV === 'development') {
            console.log("Dashboard: Preparing TOTAL count query (userId only - includes deleted)...");
          }          const totalSnapshot = await getCountFromServer(totalQuery);
          const count = totalSnapshot.data().count;
          if (process.env.NODE_ENV === 'development') {
            console.log(`Dashboard: TOTAL count query returned: ${count}`);
          }
          setTotalInvoiceCount(count);

        } catch (err) {
          console.error("Dashboard: Error fetching TOTAL INVOICE COUNT:", err);          if (err.code === 'failed-precondition') {
             // Index suggestion for the revised query
             if (process.env.NODE_ENV === 'development') {
               console.error("Dashboard Counts Error: Firestore index missing for total count query (userId ASC). Check console for Firestore link.");
             }             // Don't necessarily set a blocking error for this count
           } else {
             if (process.env.NODE_ENV === 'development') {
               console.error(`Dashboard Total Count Error Details: Code: ${err.code}, Message: ${err.message}`);
             }
           }
        } finally {
          setLoadingTotalCount(false);
        }
      };

      fetchDashboardData();    } else {
      // Log why data isn't being fetched
      if (process.env.NODE_ENV === 'development') {
        if (!isPro) console.log("Dashboard: Not fetching data - User is not Pro or subscription inactive.");
        if (!user?.uid) console.log("Dashboard: Not fetching data - User ID not available.");
      }
    }
    // Add subscriptionStatus to dependency array if used directly in effect, though isPro covers it
  }, [isPro, user?.uid, subscriptionStatus]);

  // Effect to update local state when currentUser changes
  useEffect(() => {
    if (currentUser) {
      // Directly set top-level registration details
      setGdcExpiry(currentUser.gdcExpiryDate || null);
      setGdcProvider(currentUser.gdcProvider || 'General Dental Council');
      setInsuranceExpiry(currentUser.insuranceExpiryDate || null);
      setInsuranceProvider(currentUser.insuranceProvider ? decryptData(currentUser.insuranceProvider) : null);
    } else {
      setGdcExpiry(null);
      setGdcProvider('General Dental Council');
      setInsuranceExpiry(null);
      setInsuranceProvider(null);
    }
  }, [currentUser]);

  const formatFirestoreDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    try {
      // Assuming createdAt is also a Firestore Timestamp or compatible format
      const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);
      return format(date, 'dd MMM yyyy');
    } catch (e) {
      console.error("Error formatting date (createdAt):", dateValue, e);
      return 'Invalid Date';
    }
  };

  const formatCurrency = (amount) => `£${(amount || 0).toFixed(2)}`;

  // Helper to format subscription end date
  const formatSubscriptionEndDate = (timestamp) => {
    if (!timestamp) return null;
    // Ensure timestamp is a number before creating a Date object
    const numericTimestamp = typeof timestamp === 'object' && timestamp._seconds ? timestamp._seconds : Number(timestamp);
    if (isNaN(numericTimestamp)) {
      console.warn("formatSubscriptionEndDate received non-numeric timestamp:", timestamp);
      return "Invalid Date";
    }
    const date = new Date(numericTimestamp * 1000); // Assuming timestamp is in seconds
    return `ends ${formatDistanceToNow(date, { addSuffix: true })} (${format(date, 'dd MMM yyyy')})`;
  };

  // --- Registration Alert Logic ---
  const getStorageKey = (type, urgency) => `dismissed_${type}_${urgency}_alert`;

  // --- Fetch Professional Registrations ---
  useEffect(() => {
    if (user?.uid) {
      //console.log("Setting up registration data listener for top-level fields");
      setLoadingRegistrations(true);
      const userDocRef = doc(db, 'users', user.uid);

      const unsubscribe = onSnapshot(userDocRef, (docSnap) => {        if (docSnap.exists()) {
          const data = docSnap.data();
          if (process.env.NODE_ENV === 'development') {
            console.log("Received updated user document for registrations:", {
              gdcExpiryDate: data.gdcExpiryDate,
              gdcProvider: data.gdcProvider,
              insuranceExpiryDate: data.insuranceExpiryDate,
              insuranceProvider: data.insuranceProvider,
            });
          }
          // Update state with top-level fields
          setGdcExpiry(data.gdcExpiryDate || null);
          setGdcProvider(data.gdcProvider || 'General Dental Council');
          setInsuranceExpiry(data.insuranceExpiryDate || null);
          setInsuranceProvider(data.insuranceProvider ? decryptData(data.insuranceProvider) : null);
          
          // Evaluate alerts whenever data changes
          evaluateRegistrationAlerts(
            data.gdcExpiryDate, 
            data.insuranceExpiryDate
          );
        } else {
          //console.log("User document does not exist for registrations");
          setGdcExpiry(null);
          setGdcProvider('General Dental Council');
          setInsuranceExpiry(null);
          setInsuranceProvider(null);
          setActiveRegistrationAlerts([]); // Clear alerts if user doc doesn't exist
        }
        setLoadingRegistrations(false);
      }, (error) => {
        console.error("Error fetching professional registrations (top-level):", error);
        toast({
          title: "Error",
          description: "Could not load registration details.",
          variant: "destructive",
        });
        setActiveRegistrationAlerts([]); // Clear alerts on error
        setLoadingRegistrations(false);
      });

      return () => {
        //console.log("Cleaning up registration data listener (top-level)");
        unsubscribe();
      };
    } else {
      setLoadingRegistrations(false);
      setGdcExpiry(null);
      setGdcProvider('General Dental Council');
      setInsuranceExpiry(null);
      setInsuranceProvider(null);
    }
  }, [user?.uid, toast]); // Removed evaluateRegistrationAlerts from dependencies here

  // Separate function to evaluate registration alerts based on top-level dates
  const evaluateRegistrationAlerts = (gdcExpiryDate, insuranceExpiryDate) => {
    const gdcStatus = calculateStatus(gdcExpiryDate);
    const insuranceStatus = calculateStatus(insuranceExpiryDate);

    let alertsToShow = []; // Initialize as an array

    // Check GDC
    if (['expired', 'urgent', 'soon'].includes(gdcStatus.urgency)) {
      const storageKey = getStorageKey('gdc', gdcStatus.urgency);
      const dismissed = localStorage.getItem(storageKey);
      if (!dismissed) {
        let message = '';
        if (gdcStatus.urgency === 'expired') message = 'GDC registration has expired!';
        else if (gdcStatus.urgency === 'urgent') message = 'GDC registration expires in less than 7 days!';
        else if (gdcStatus.urgency === 'soon') message = 'GDC registration expires in less than 30 days.';
        alertsToShow.push({ type: 'GDC', message, urgency: gdcStatus.urgency });
      }
    }

    // Check Insurance independently
    if (['expired', 'urgent', 'soon'].includes(insuranceStatus.urgency)) {
      const storageKey = getStorageKey('insurance', insuranceStatus.urgency);
      const dismissed = localStorage.getItem(storageKey);
      if (!dismissed) {
        let message = '';
        if (insuranceStatus.urgency === 'expired') message = 'Insurance registration has expired!';
        else if (insuranceStatus.urgency === 'urgent') message = 'Insurance registration expires in less than 7 days!';
        else if (insuranceStatus.urgency === 'soon') message = 'Insurance registration expires in less than 30 days.';
        alertsToShow.push({ type: 'Insurance', message, urgency: insuranceStatus.urgency });
      }
    }

    // Update state with the array of active, non-dismissed alerts
    setActiveRegistrationAlerts(alertsToShow);
  };

  // useEffect to run evaluation when registration dates change
  useEffect(() => {
    evaluateRegistrationAlerts(gdcExpiry, insuranceExpiry);
  }, [gdcExpiry, insuranceExpiry]);


  // --- Modal Handling ---
  const handleOpenRegistrationModal = (type) => {
    setCurrentRegistrationType(type);
    setIsRegistrationModalOpen(true);
  };

  const handleCloseRegistrationModal = () => {
    setIsRegistrationModalOpen(false);
    setCurrentRegistrationType(null);
  };

  const handleRegistrationUpdateSuccess = (updatedData) => {
    //console.log("Registration update successful, data:", updatedData);
    handleCloseRegistrationModal();

    // Clear relevant localStorage dismissal flags when data is updated
    const keysToClear = ['expired', 'urgent', 'soon', 'upcoming'];
    keysToClear.forEach(urgency => {
      localStorage.removeItem(getStorageKey(currentRegistrationType, urgency));
    });

    // Provide visual feedback for the update
    toast({
      title: "Success",
      description: `${currentRegistrationType === 'gdc' ? 'GDC' : 'Insurance'} details updated.`,
      variant: "success",
    });

    // The onSnapshot listener will trigger a state update and re-evaluation
    // No need to manually call evaluateRegistrationAlerts here if snapshot listener is working
  };
  // --- Professional Registrations Display Component ---
  const ProfessionalRegistrationsDisplay = ({ 
    gdcExpiryDate, 
    gdcRegProvider, // Renamed to avoid conflict with component state
    insuranceExpiryDate, 
    insuranceRegProvider, // Renamed
    onUpdateClick 
  }) => {
    
    const gdcStatus = calculateStatus(gdcExpiryDate);
    const insuranceStatus = calculateStatus(insuranceExpiryDate);

    const RegistrationCard = ({ title, expiry, provider, status, type }) => {
      // Define card styles based on urgency and theme
      const cardClasses = cn(
        "p-4 rounded-lg border", // Base styles without transition initially
        // Apply transition only if not urgent or expired
        !['urgent', 'expired'].includes(status.urgency) && "transition-all duration-300",
        // Theme-specific base styles
        theme === 'light' ? 'bg-white border-gray-200' : 'bg-[#1F1F2E] border-purple-500/20',
        // Add dashed border for 'Not Set' state
        status.urgency === 'none' && theme === 'light' && 'border-dashed border-gray-400',
        status.urgency === 'none' && theme === 'dark' && 'border-dashed border-gray-600',
        // Urgency-based overrides (Light Theme)
        status.urgency === 'expired' && theme === 'light' && 'bg-red-200 border-red-500 animate-pulse-slow', // Add pulse
        status.urgency === 'urgent' && theme === 'light' && 'bg-red-100 border-red-400 animate-pulse-slow', // Keep pulse
        status.urgency === 'soon' && theme === 'light' && 'bg-red-100 border-red-300',
        status.urgency === 'upcoming' && theme === 'light' && 'bg-yellow-50 border-yellow-300',
        // Urgency-based overrides (Dark Theme)
        status.urgency === 'expired' && theme === 'dark' && 'bg-red-800/50 border-red-400/70 animate-pulse-slow', // Add pulse
        status.urgency === 'urgent' && theme === 'dark' && 'bg-red-900/50 border-red-500/70 animate-pulse-slow', // Keep pulse
        status.urgency === 'soon' && theme === 'dark' && 'bg-red-900/30 border-red-500/50',
        status.urgency === 'upcoming' && theme === 'dark' && 'bg-yellow-900/30 border-yellow-500/50'
      );

      // Define text colors based on urgency and theme - Adjust for brighter expired background
      const primaryTextColor = cn(
        theme === 'light' ? 'text-gray-800' : 'text-white',
        status.urgency === 'expired' && theme === 'light' && 'text-red-900', // Darker text on brighter red
        status.urgency === 'urgent' && theme === 'light' && 'text-red-800',
        status.urgency === 'soon' && theme === 'light' && 'text-red-700',
        status.urgency === 'upcoming' && theme === 'light' && 'text-yellow-800',
        status.urgency === 'expired' && theme === 'dark' && 'text-red-100', // Lighter text on brighter red
        status.urgency === 'urgent' && theme === 'dark' && 'text-red-100',
        status.urgency === 'soon' && theme === 'dark' && 'text-red-300',
        status.urgency === 'upcoming' && theme === 'dark' && 'text-yellow-300'
      );
      const secondaryTextColor = cn(
         theme === 'light' ? 'text-gray-600' : 'text-gray-300',
         status.urgency === 'expired' && theme === 'light' && 'text-red-800', // Darker text
         status.urgency === 'urgent' && theme === 'light' && 'text-red-700',
         status.urgency === 'soon' && theme === 'light' && 'text-red-600',
         status.urgency === 'upcoming' && theme === 'light' && 'text-yellow-700',
         status.urgency === 'expired' && theme === 'dark' && 'text-red-200', // Lighter text
         status.urgency === 'urgent' && theme === 'dark' && 'text-red-200',
         status.urgency === 'soon' && theme === 'dark' && 'text-red-400',
         status.urgency === 'upcoming' && theme === 'dark' && 'text-yellow-400'
      );
       const tertiaryTextColor = cn(
         theme === 'light' ? 'text-gray-500' : 'text-gray-400',
         status.urgency === 'expired' && theme === 'light' && 'text-red-700', // Darker text
         status.urgency === 'urgent' && theme === 'light' && 'text-red-600',
         status.urgency === 'soon' && theme === 'light' && 'text-red-500',
         status.urgency === 'upcoming' && theme === 'light' && 'text-yellow-600',
         status.urgency === 'expired' && theme === 'dark' && 'text-red-300', // Lighter text
         status.urgency === 'urgent' && theme === 'dark' && 'text-red-300',
         status.urgency === 'soon' && theme === 'dark' && 'text-red-500',
         status.urgency === 'upcoming' && theme === 'dark' && 'text-yellow-500'
      );
      // Define prompt text color based on urgency and theme
      const promptTextColor = cn(
        'text-xs font-medium mt-1', // Base style for prompt
        status.urgency === 'expired' && theme === 'light' && 'text-red-700',
        status.urgency === 'urgent' && theme === 'light' && 'text-red-600',
        status.urgency === 'soon' && theme === 'light' && 'text-red-600',
        status.urgency === 'upcoming' && theme === 'light' && 'text-yellow-700',
        status.urgency === 'none' && theme === 'light' && 'text-gray-500', // Prompt color for 'Not Set'
        status.urgency === 'expired' && theme === 'dark' && 'text-red-200',
        status.urgency === 'urgent' && theme === 'dark' && 'text-red-200',
        status.urgency === 'soon' && theme === 'dark' && 'text-red-300',
        status.urgency === 'upcoming' && theme === 'dark' && 'text-yellow-300',
        status.urgency === 'none' && theme === 'dark' && 'text-gray-400' // Prompt color for 'Not Set'
      );

      // Updated GDC URL
      const GDC_RENEWAL_URL = "https://www.gdc-uk.org/registration/annual-renewal-and-fees";

      return (
        // Apply the calculated card classes
        <div className={cardClasses}>
          <div className="flex justify-between items-start mb-2">
            {/* Container for Title and Badge */}
            <div className="flex items-center space-x-2 flex-grow mr-2"> {/* Added flex-grow and margin */}
              {/* Apply theme/urgency-aware text color */}
              <h4 className={cn("font-semibold", primaryTextColor)}>{title}</h4>
              {/* Status badge moved here */}
              <div className={`text-xs font-medium px-2 py-0.5 rounded-full inline-block ${status.colorClass}`}>
                {status.text}
              </div>
            </div>
            {/* Edit Button styling remains consistent - Add tooltip */}
            <Button variant="ghost" size="sm" className="h-7 px-2 text-accent-foreground hover:bg-white/10 shrink-0" onClick={() => onUpdateClick(type)} title="Update Details"> {/* Added shrink-0 */}
              <Edit size={14} />
            </Button>
          </div>
          {/* Main content area */}
          {expiry ? (
            <div className="space-y-1">
              {/* Apply theme/urgency-aware text color */}
              <p className={cn("text-sm", secondaryTextColor)}>
                Expires: {format(expiry.toDate ? expiry.toDate() : new Date(expiry), 'dd MMM yyyy')}
              </p>
              {/* Apply theme/urgency-aware text color */}
              <p className={cn("text-xs", tertiaryTextColor)}>
                {status.daysRemaining !== null ?
                  (status.daysRemaining < 0 ? `${Math.abs(status.daysRemaining)} days ago` : `${status.daysRemaining} days remaining`)
                  : ''}
              </p>
              {provider && provider !== "General Dental Council" && (
                // Apply theme/urgency-aware text color
                <p className={cn("text-xs pt-1", tertiaryTextColor)}>Provider: {provider}</p>
              )}
            </div>
          ) : (
            // Apply theme/urgency-aware text color
            <p className={cn("text-sm", secondaryTextColor)}>Expiry date not set.</p>
          )}

          {/* Add the explicit prompt text if it exists */}
          {status.prompt && (
            // Use flex container for prompt and button alignment, allow wrapping
            <div className="flex flex-wrap items-center mt-2 space-x-1.5"> {/* Added mt-2 for spacing */}
              <p className={promptTextColor}>
                {status.prompt}
              </p>
              {/* Add GDC link as a small button with icon */}
              {type === 'gdc' && ['expired', 'urgent', 'soon', 'upcoming'].includes(status.urgency) && (
                <Button
                  asChild // Use asChild to render the anchor tag within the button styles
                  variant="outline"
                  size="xs" // Use a smaller size if available, or adjust padding
                  className={cn(
                    "h-6 px-2 text-xs shrink-0", // Added shrink-0
                    theme === 'light'
                      ? 'border-purple-300 text-purple-700 hover:bg-purple-50'
                      : 'border-purple-500/40 text-purple-300 hover:bg-purple-500/10'
                  )}
                >
                  <a
                    href={GDC_RENEWAL_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Go to GDC Renewal Page"
                    className="flex items-center" // Add flex for icon alignment
                  >
                    <ExternalLink size={12} className="mr-1" /> {/* Add icon */}
                    Renew Here
                  </a>
                </Button>
              )}
              {/* Add Resources link as a small button with icon */}
              {type === 'insurance' && ['expired', 'urgent', 'soon', 'upcoming'].includes(status.urgency) && (
                 <Button
                   variant="outline"
                   size="xs" // Use a smaller size if available, or adjust padding
                   className={cn(
                     "h-6 px-2 text-xs shrink-0", // Added shrink-0
                     theme === 'light'
                       ? 'border-purple-300 text-purple-700 hover:bg-purple-50'
                       : 'border-purple-500/40 text-purple-300 hover:bg-purple-500/10'
                   )}
                   onClick={() => navigate(ROUTES.RESOURCES)}
                   title="Go to Resources Page"
                 >
                   <BookOpen size={12} className="mr-1" /> {/* Add icon */}
                   See Resources
                 </Button>
              )}
            </div>
          )}
        </div>
      );
    };

    return (
      // Main container card styling remains consistent
      <Card className={cn(
        "lg:col-span-3", // Span across all 3 columns
        theme === 'light' ? 'bg-gray-50 border-gray-200' : 'app-accent-purple-bg-light border-border'
      )}>
        <CardHeader>
          {/* Title styling remains consistent */}
          <CardTitle className={cn(theme === 'light' ? 'text-gray-900' : 'text-accent-foreground')}>
            <ShieldCheck className="inline-block mr-2" size={20} />
            Professional Registrations
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <RegistrationCard 
            title="GDC Registration" 
            expiry={gdcExpiryDate} 
            provider={gdcRegProvider} 
            status={gdcStatus} 
            type="gdc" 
          />
          <RegistrationCard 
            title="Indemnity Insurance" 
            expiry={insuranceExpiryDate} 
            provider={insuranceRegProvider} 
            status={insuranceStatus} 
            type="insurance" 
          />
        </CardContent>
      </Card>
    );
  };
  // --- End Professional Registrations Display Component ---


  const QuickActionButton = ({ to, icon, label, proFeature = false, alwaysAccessible = false }) => {
    const proDisabled = proFeature && !isPro;
    const freeDisabled = isSubscriptionEffectivelyFree && !alwaysAccessible;
    const isDisabled = proDisabled || freeDisabled;
    
    let target = to;
    let buttonTitle = label;

    if (isDisabled) {
      target = ROUTES.BILLING; // Redirect to billing if disabled
      if (proDisabled) {
        buttonTitle = `Upgrade to Pro to access ${label}`;
      } else if (freeDisabled) {
        buttonTitle = `Subscribe to access ${label}`;
      }
    }
    
    // ... rest of QuickActionButton styling logic from your file ...
    const baseClasses = "group flex flex-col items-center justify-center h-24 w-full text-center p-2 space-y-1 transition-colors border rounded-lg";    const enabledClasses = cn(
      theme === 'light' 
        ? 'bg-white border-gray-200 text-purple-700 hover:bg-purple-50 hover:border-purple-300' 
        : 'app-accent-purple-bg-light app-accent-purple border-border hover:app-accent-purple-bg hover:text-white'
    );
    const disabledClasses = cn(
      "cursor-not-allowed",
      theme === 'light' 
        ? 'bg-gray-100 text-gray-400 border-gray-200' 
        : 'bg-muted text-muted-foreground border-border'
    );
    const iconEnabledClasses = cn(theme === 'light' ? 'text-purple-600 group-hover:text-purple-700' : 'text-accent-foreground group-hover:text-white');
    const iconDisabledClasses = cn(theme === 'light' ? 'text-gray-400' : 'text-muted-foreground');
    const textEnabledClasses = cn(theme === 'light' ? 'text-purple-700 group-hover:text-purple-800' : 'text-accent-foreground group-hover:text-white');
    const textDisabledClasses = cn(theme === 'light' ? 'text-gray-400' : 'text-muted-foreground');

    return (
      <Button
        variant="outline"
        className={`${baseClasses} ${isDisabled ? disabledClasses : enabledClasses}`}
        onClick={() => {
          if (isDisabled) {
            toast({
              title: proDisabled ? "Pro Feature" : "Subscription Required",
              description: buttonTitle,
              variant: "warning",
            });
          }
          navigate(target);
        }}
        title={buttonTitle}
      >
        {React.cloneElement(icon, { className: `${icon.props.className || ''} transition-colors ${isDisabled ? iconDisabledClasses : iconEnabledClasses}` })}
        <span className={`text-xs sm:text-sm transition-colors ${isDisabled ? textDisabledClasses : textEnabledClasses}`}>{label}</span>
        {/* FIXED: Only show PRO badge for pro features that are disabled (user doesn't have Pro) */}
        {proDisabled && (
          <span className={`text-[10px] ${theme === 'light' ? 'text-gray-400' : 'text-muted-foreground'}`}>
            (PRO)
          </span>
        )}
      </Button>
    );
  };


  // Modify the dismiss handler to accept type and urgency
  const handleDismissRegistrationAlert = (type, urgency) => {
    const storageKey = getStorageKey(type.toLowerCase(), urgency);
    // Use localStorage for dismissal persistence
    localStorage.setItem(storageKey, 'true');
    // Update the state to remove the dismissed alert
    setActiveRegistrationAlerts(prevAlerts =>
      prevAlerts.filter(alert => !(alert.type.toLowerCase() === type.toLowerCase() && alert.urgency === urgency))
    );
  };

  return (
    <div className="container mx-auto p-4 space-y-6">
      {/* Registration Alert Banners - Map over the active alerts */}
      {activeRegistrationAlerts.map((alert, index) => (
        <Alert
          key={`${alert.type}-${alert.urgency}-${index}`} // Unique key for each alert
          variant="destructive"
          className={cn(
            "relative flex items-center justify-between", // Make sure there's space for the button
            theme === 'light'
              ? 'bg-red-100 border-red-400 text-red-800'
              : 'bg-red-900/30 border-red-500/50 text-red-100'
          )}
        >
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-4 w-4" />
            <div>
              {/* Use the specific alert type in the title */}
              <AlertTitle>{alert.type} Registration Alert</AlertTitle>
              <AlertDescription>
                {alert.message} Please update your details.
              </AlertDescription>
            </div>
          </div>
          {/* Pass specific type and urgency to the dismiss handler */}
          <Button
            onClick={() => handleDismissRegistrationAlert(alert.type, alert.urgency)}
            className={cn(
              "ml-2 px-2 py-1 rounded h-8 w-8 flex items-center justify-center", // Make button more visible
              theme === 'light'
                ? "bg-red-200 hover:bg-red-300 text-red-800 border border-red-300"
                : "bg-red-800 hover:bg-red-700 text-white border border-red-500"
            )}
            aria-label={`Dismiss ${alert.type} alert`}
          >
            <CloseIcon className="h-4 w-4" />
          </Button>
        </Alert>
      ))}

      {/* Welcome Message */}
      <h1 className={cn(
        "text-2xl sm:text-3xl font-bold",
         theme === 'light' ? 'text-gray-900' : 'text-accent-foreground'
      )}>
        Welcome, {currentUser?.name || user?.displayName || user?.email || 'User'}!
      </h1>
      <NotificationDisplay user={user} />

      {error && (
        // Error alert theme handling
        <div className={cn(
          "p-3 rounded-md flex items-center gap-2 border",
          theme === 'light' 
            ? 'bg-red-50 border-red-200 text-red-700' 
            : 'bg-red-900/30 border-red-500/50 text-red-300'
        )}>
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}
      {/* Quick Actions */}
      {/* Apply theme-aware background and border */}
      <Card className={cn(
        theme === 'light' ? 'bg-gray-50 border-gray-200' : 'app-accent-purple-bg-light border-border'
      )}>
        <CardHeader>
          {/* Apply theme-aware text color */}
          <CardTitle className={cn(theme === 'light' ? 'text-gray-900' : 'text-accent-foreground')}>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <QuickActionButton to={ROUTES.INVOICE} icon={<FileText size={24} />} label="New Invoice" />
            <QuickActionButton to={ROUTES.EXPENSES} icon={<Receipt size={24} />} label="Log Expense" proFeature={true} />
            <QuickActionButton to={ROUTES.INVOICE_HISTORY} icon={<History size={24} />} label="View History" proFeature={true} />
            <QuickActionButton to={ROUTES.REPORTS} icon={<BarChart2 size={24} />} label="View Reports" proFeature={true} />
            <QuickActionButton to={ROUTES.TAX} icon={<Calculator size={24} />} label="Tax Estimate" proFeature={true} />
            <QuickActionButton to={ROUTES.BILLING} icon={<CreditCard size={24} />} label="Billing" alwaysAccessible={true} />
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Account Summary Card */}
        {/* Apply theme-aware background and border */}
        <Card className={cn(
          "lg:col-span-1",
          theme === 'light' ? 'bg-gray-50 border-gray-200' : 'app-accent-purple-bg-light border-border'
        )}>
          <CardHeader>
            {/* Apply theme-aware text color */}
            <CardTitle className={cn(theme === 'light' ? 'text-gray-900' : 'text-accent-foreground')}>Account Summary</CardTitle>
          </CardHeader>
          {/* Apply theme-aware text color within CardContent */}
          <CardContent className={cn(theme === 'light' ? 'text-gray-700' : 'text-card-foreground')}>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                {/* Apply theme-aware text color */}
                <span className={cn("flex items-center", theme === 'light' ? 'text-gray-500' : 'text-muted-foreground')}>
                  {subscriptionStatus?.subscription?.level === 'pro' && !isSubscriptionEffectivelyFree ? <Star size={16} className={cn("mr-2", theme === 'light' ? 'text-purple-600' : 'text-accent-foreground')} /> : <Package size={16} className={cn("mr-2", theme === 'light' ? 'text-gray-400' : 'text-muted-foreground')} />}
                  Plan:
                </span>
                <span className={cn(
                  "font-semibold capitalize",
                  subscriptionStatus?.subscription?.level === 'pro' && !isSubscriptionEffectivelyFree
                    ? (theme === 'light' ? 'text-purple-700' : 'text-accent-foreground') 
                    : (theme === 'light' ? 'text-gray-800' : 'text-card-foreground')
                )}>
                  {/* CRITICAL FIX: Handle cancel_at_period_end status with priority over other checks */}
                  {subscriptionStatus?.subscription?.cancelAtPeriodEnd && 
                   subscriptionStatus?.subscription?.status === 'active' ? 
                    `${subscriptionStatus?.subscription?.level} (Cancelling)` :
                   subscriptionStatus?.subscription?.status === 'past_due' ? 
                    `${subscriptionStatus?.subscription?.level} (Expired)` :
                    (isSubscriptionEffectivelyFree ? 'Free' : subscriptionStatus?.subscription?.level)
                  }
                  {subscriptionStatus?.subscription?.isFreeTrial && !isSubscriptionEffectivelyFree && ' (Trial)'}
                </span>
              </div>
              
              {isSubscriptionEffectivelyFree ? (
                <div className={cn("flex items-center justify-between text-xs", theme === 'light' ? 'text-red-600' : 'text-red-400')}>
                    <span className="flex items-center">
                        <AlertCircle size={14} className="mr-1.5" />
                        Status:
                    </span>
                    <span className="capitalize">
                        {/* CRITICAL FIX: Show appropriate status for past_due */}
                        {subscriptionStatus?.subscription?.status === 'past_due' ? 'Payment Required' :
                         subscriptionStatus?.subscription?.status === 'ended' || 
                         subscriptionStatus?.subscription?.status === 'canceled' ? 'Ended' : 
                         subscriptionStatus?.subscription?.status === 'incomplete_expired' ? 'Expired' :
                         'Inactive'}
                    </span>
                </div>
              ) : (subscriptionStatus?.subscription?.isFreeTrial && subscriptionStatus?.subscription?.trialEnd) || (!subscriptionStatus?.subscription?.isFreeTrial && subscriptionStatus?.subscription?.currentPeriodEnd) ? (
                <div className={cn("flex items-center justify-between text-xs", theme === 'light' ? 'text-gray-500' : 'text-muted-foreground')}>
                  <span className="flex items-center">
                    <Clock size={14} className="mr-1.5" />
                    {/* CRITICAL FIX: Enhanced period end messaging for cancel_at_period_end */}
                    {subscriptionStatus.subscription.isFreeTrial ? 'Trial ' : 
                     subscriptionStatus.subscription.cancelAtPeriodEnd ? 'Access ends: ' : 'Renews on: '}
                  </span>
                  <span>
                    {formatSubscriptionEndDate(subscriptionStatus.subscription.isFreeTrial ? subscriptionStatus.subscription.trialEnd : subscriptionStatus.subscription.currentPeriodEnd)}
                  </span>
                </div>
              ) : subscriptionStatus?.subscription?.status && subscriptionStatus.subscription.status !== 'active' && subscriptionStatus.subscription.status !== 'trialing' ? (
                <div className={cn("flex items-center justify-between text-xs", theme === 'light' ? 'text-red-600' : 'text-red-400')}>
                  <span className="flex items-center">
                    <AlertCircle size={14} className="mr-1.5" />
                    Status:
                  </span>
                  <span className="capitalize">{subscriptionStatus.subscription.status}</span>
                </div>
              ) : null}

              {/* ... All Invoices count ... */}
            </div>
            
            {/* FIXED: Add container with fixed height to prevent layout shifts */}
            <div className="mt-4 min-h-[40px]"> {/* Add min-height to prevent disappearing */}
              <Button 
                className={cn(
                  "w-full",
                  // Strengthen button visibility with more explicit styles
                  theme === 'light' 
                    ? "border border-gray-300 bg-white text-gray-800 hover:bg-gray-100" 
                    : "border border-gray-600 bg-gray-800 text-white hover:bg-gray-700",
                  // Add important to prevent style overrides
                  "!opacity-100" // Force full opacity
                )}
                variant="outline" 
                onClick={() => navigate(ROUTES.BILLING)}
              >
                {isSubscriptionEffectivelyFree ? 'Subscribe Now' : 'Manage Subscription'}
              </Button>
              {/* SURGICAL: Add static note about subscription update delay */}
              <div className="mt-1 text-[11px] text-gray-400 dark:text-gray-500 text-center">
                  <span className="block mt-1">If your subscription status does not update after a few minutes, please log out and log back in to refresh your account.</span>
              </div>
              {/* SURGICAL: Show Stripe sync note if payment=success in query param */}
              {showStripeSyncNote && (
                <div className="mt-2 text-xs text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 rounded p-2 border border-blue-200 dark:border-blue-700">
                  <strong>Thank you for your payment!</strong><br />
                  Your subscription may take up to 10 minutes to be fully activated. If you don’t see your new plan soon, please refresh this page.
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Invoices / Upgrade Prompt */}
        {isPro ? (
          // Apply theme-aware background and border
          <Card className={cn(
            "lg:col-span-2",
            theme === 'light' ? 'bg-gray-50 border-gray-200' : 'app-accent-purple-bg-light border-border'
          )}>
            <CardHeader>
              {/* Apply theme-aware text color */}
              <CardTitle className={cn(theme === 'light' ? 'text-gray-900' : 'text-accent-foreground')}>Recent Invoices</CardTitle>
            </CardHeader>
            <CardContent>
              {loadingInvoices ? (
                <div className="flex justify-center items-center h-32">
                  <LoadingSpinner />
                </div>
              ) : recentInvoices.length > 0 ? (
                <ul className="space-y-3">
                  {recentInvoices.map(invoice => {
                    // Determine badge style based on status and theme
                    let badgeStyle = {};
                    const baseBadgeClass = "text-xs px-2 py-0.5 rounded-full border";
                    let badgeClasses = baseBadgeClass;

                    if (invoice.isPaid) {
                      badgeClasses = cn(baseBadgeClass, theme === 'light' ? 'bg-green-100 border-green-300 text-green-800' : 'bg-green-900/30 border-green-500/50 text-green-300');
                    } else if (invoice.dueDate && isPast(invoice.dueDate.toDate ? invoice.dueDate.toDate() : new Date(invoice.dueDate))) {
                      badgeClasses = cn(baseBadgeClass, theme === 'light' ? 'bg-red-100 border-red-300 text-red-800' : 'bg-red-900/30 border-red-500/50 text-red-300');
                    } else {
                      badgeClasses = cn(baseBadgeClass, theme === 'light' ? 'bg-yellow-100 border-yellow-300 text-yellow-800' : 'bg-yellow-900/30 border-yellow-500/50 text-yellow-300');
                    }

                    return (
                      // Apply theme-aware background and border on list item
                      <li key={invoice.id} className={cn(
                        "flex justify-between items-center p-3 rounded-md border group transition-colors",
                        theme === 'light' 
                          ? 'bg-white border-gray-200 hover:bg-purple-50 hover:border-purple-200' 
                          : 'border-border hover:app-accent-purple-bg'
                      )}>
                        <div>
                          {/* Apply theme-aware text color */}
                          <p className={cn(
                            "text-sm font-medium transition-colors",
                            theme === 'light' ? 'text-gray-800 group-hover:text-purple-800' : 'text-card-foreground group-hover:text-white'
                          )}>
                            {invoice.practiceDetails?.prac_name || invoice.prac_name || 'Unknown Practice'}
                          </p>
                          {/* Apply theme-aware text color */}
                          <p className={cn(
                            "text-xs transition-colors",
                            theme === 'light' ? 'text-gray-500 group-hover:text-purple-600' : 'text-muted-foreground group-hover:text-gray-200'
                          )}>
                            {formatFirestoreDate(invoice.createdAt)} - {formatCurrency(invoice.totalAmount)}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={badgeClasses}>
                            {invoice.isPaid ? 'Paid' : (invoice.dueDate && isPast(invoice.dueDate.toDate ? invoice.dueDate.toDate() : new Date(invoice.dueDate)) ? 'Overdue' : 'Unpaid')}
                          </span>
                          {/* Button theme handled by Shadcn/globals */}
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-accent-foreground group-hover:text-white hover:bg-white/10" onClick={() => navigate(`${ROUTES.INVOICE_DETAILS.replace(':id', '')}${invoice.id}`)}>
                            <ExternalLink size={14} />
                          </Button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                 <p className={cn("text-center py-4", theme === 'light' ? 'text-gray-500' : 'text-muted-foreground')}>No recent non-deleted invoices found.</p>
              )}
            </CardContent>
          </Card>
        ) : (
          // Apply theme-aware background and border
          <Card className={cn(
            "lg:col-span-2",
            theme === 'light' ? 'bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200' : 'app-accent-purple-bg-light border-border'
          )}>
            <CardHeader>
              {/* Apply theme-aware text color */}
              <CardTitle className={cn(theme === 'light' ? 'text-purple-800' : 'text-green-400')}>Unlock Pro Features</CardTitle>
              {/* Apply theme-aware text color */}
              <CardDescription className={cn(theme === 'light' ? 'text-purple-600' : 'text-muted-foreground')}>
                Upgrade to Pro to access powerful tools like detailed reports, full invoice history, expense tracking, and tax estimation.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col sm:flex-row items-center justify-between gap-4">
              {/* Apply theme-aware text color */}
              <ul className={cn("list-disc list-inside space-y-1 text-sm", theme === 'light' ? 'text-purple-700' : 'text-muted-foreground')}>
                <li>Detailed Income Reports</li>
                <li>Full Invoice History & Search</li>
                <li>Expense & Mileage Tracking</li>
                <li>Tax Liability Estimation</li>
                <li>Priority Support</li>
              </ul>
              {/* Button theme handled by Shadcn/globals */}
              <Button
                className="bg-green-600 hover:bg-green-700 text-white mt-4 sm:mt-0 w-full sm:w-auto"
                onClick={() => navigate(ROUTES.BILLING)}
              >
                Upgrade to Pro
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Render Professional Registrations */}
        <ProfessionalRegistrationsDisplay
          gdcExpiryDate={gdcExpiry}
          gdcRegProvider={gdcProvider}
          insuranceExpiryDate={insuranceExpiry}
          insuranceRegProvider={insuranceProvider}
          onUpdateClick={handleOpenRegistrationModal}
        />

      </div>

      {/* Render the Modal */}
      <RegistrationRenewalModal
        isOpen={isRegistrationModalOpen}
        onClose={handleCloseRegistrationModal}
        registrationType={currentRegistrationType}
        // Pass current data based on type from top-level state
        currentData={
          currentRegistrationType === 'gdc' 
            ? { expiryDate: gdcExpiry, provider: gdcProvider } 
            : { expiryDate: insuranceExpiry, provider: insuranceProvider }
        }
        userId={user?.uid}
        onUpdateSuccess={handleRegistrationUpdateSuccess}
      />
    </div>
  );
};

export default DashboardPage;
